import axios from "axios";
import { createContext, ReactNode, useCallback, useContext, useState, useEffect } from "react";

import scraperApi, { JobOrder } from "api";

import { Job, ProjectConfigWithStats, RunningProjectInfo } from "./types";

import { useUser } from "routes/dataroutes/UserData";


export const PROJECT_PAGE_SIZE = 25;

// Client-side sorting fallback for when backend doesn't support orderBy (master)
function reverse<T>(f: (a: T, b: T) => number): (a: T, b: T) => number {
  return (a, b) => f(b, a);
}

function composeComparators<T>(fnPrimary: (a: T, b: T) => number, fnSecondary: (a: T, b: T) => number): (a: T, b: T) => number {
  return (a, b) => {
    const winner = fnPrimary(a, b);
    return winner === 0 ? fnSecondary(a, b) : winner;
  };
}

const compareByLastRun = (a: ProjectConfigWithStats, b: ProjectConfigWithStats) => {
  return a.lastRunAt === null && b.lastRunAt === null
    ? 0
    : a.lastRunAt === null
    ? -1
    : b.lastRunAt === null
    ? 1
    : new Date(a.lastRunAt).getTime() - new Date(b.lastRunAt).getTime();
};

const compareByEnabled = (a: ProjectConfigWithStats, b: ProjectConfigWithStats) => {
  return a.enabled === b.enabled ? 0 : a.enabled ? -1 : 1;
};

const compareByCreatedAt = (a: ProjectConfigWithStats, b: ProjectConfigWithStats) => {
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
};

function orderProjectsClientSide(projects: ProjectConfigWithStats[], order: string): ProjectConfigWithStats[] {
  const sorted = [...projects];
  switch (order) {
    case 'last_run_first':
      return sorted.sort(reverse(compareByLastRun));
    case 'last_run_last':
      return sorted.sort(compareByLastRun);
    case 'last_created_first':
      return sorted.sort(reverse(compareByCreatedAt));
    case 'last_created_last':
      return sorted.sort(compareByCreatedAt);
    case 'scheduled_first':
      return sorted.sort(composeComparators(compareByEnabled, compareByCreatedAt));
    case 'scheduled_last':
      return sorted.sort(composeComparators(reverse(compareByEnabled), compareByCreatedAt));
    default:
      return sorted;
  }
}

export type HostedScrapingProjectsContextType = {
  refreshInBackground: (controller: AbortController | undefined) => Promise<void>;
  inProgress: boolean;
  projects: ProjectConfigWithStats[];
  runningProjectInfo?: RunningProjectInfo;
  totalCount: number;
  page: number;
  setPage: (page: number) => void;
  orderBy: string;
  setOrderBy: (orderBy: string) => void;
  currentProject: ProjectConfigWithStats | undefined;
  currentProjectLoading: boolean;
  fetchCurrentProject: (projectId: number) => Promise<void>;
  clearCurrentProject: () => void;
  jobs: Job[] | null;
  jobsLoading: boolean;
  jobOrder: JobOrder;
  setJobOrder: (order: JobOrder) => void;
  numberOfJobs: number;
  setNumberOfJobs: (n: number) => void;
  fetchJobs: (projectId: number) => Promise<void>;
};

const HostedScrapingProjectsContext = createContext<HostedScrapingProjectsContextType>(null!);


export default function HostedScrapingProjectsProvider({ children }: { children: ReactNode }) {

  const [ projects, setProjects ] = useState<ProjectConfigWithStats[]>([]);
  const [ inProgress, setInProgress ] = useState<boolean>(true);
  const [ totalCount, setTotalCount ] = useState<number>(0);
  const [ page, setPage ] = useState<number>(0);
  const [ orderBy, setOrderBy ] = useState<string>('last_run_first');
  const [ runningProjectInfo, setRunningProjectInfo ] = useState<RunningProjectInfo | undefined>(undefined);

  const [ currentProject, setCurrentProject ] = useState<ProjectConfigWithStats | undefined>(undefined);
  const [ currentProjectLoading, setCurrentProjectLoading ] = useState<boolean>(false);

  const [ jobs, setJobs ] = useState<Job[] | null>(null);
  const [ jobsLoading, setJobsLoading ] = useState<boolean>(false);
  const [ jobOrder, setJobOrder ] = useState<JobOrder>('last_run_first');
  const [ numberOfJobs, setNumberOfJobs ] = useState<number>(5);

  const user = useUser();

  const fetchProjects = useCallback(
    async (controller?: AbortController) => {
      const offset = page * PROJECT_PAGE_SIZE;
      const [response, runningInfo] = await Promise.all([
        scraperApi.hostedScraping.projects(
          PROJECT_PAGE_SIZE,
          offset,
          orderBy,
          { signal: controller?.signal }
        ),
        scraperApi.hostedScraping.getRunningProjectInfo({ signal: controller?.signal }),
      ]);
      // Handle both old format (plain array) and new format ({ projects, totalCount })
      if (Array.isArray(response)) {
        // Old backend: no server-side sort/pagination, apply client-side
        setProjects(orderProjectsClientSide(response, orderBy));
        setTotalCount(response.length);
      } else if (response) {
        // New backend: sort and pagination handled server-side
        setProjects(response.projects);
        setTotalCount(response.totalCount);
      }
      setRunningProjectInfo(runningInfo);
    },
    [ page, orderBy ]
  );

  const fetchJobs = useCallback(
    async (projectId: number) => {
      setJobsLoading(true);
      try {
        const fetchedJobs = await scraperApi.hostedScraping.jobs(projectId, numberOfJobs, jobOrder);
        setJobs(fetchedJobs);
      } finally {
        setJobsLoading(false);
      }
    },
    [numberOfJobs, jobOrder]
  );

  const fetchCurrentProject = useCallback(
    async (projectId: number) => {
      setCurrentProjectLoading(true);
      try {
        const [project, runningInfo] = await Promise.all([
          scraperApi.hostedScraping.singleProject(projectId),
          scraperApi.hostedScraping.getRunningProjectInfo(),
          fetchJobs(projectId),
        ]);
        setCurrentProject(project);
        setRunningProjectInfo(runningInfo);
        // Merge into list if present (don't append if not on current page)
        setProjects(prev => {
          const index = prev.findIndex(p => p.id === projectId);
          if (index === -1) return prev;
          const updated = [ ...prev ];
          updated[index] = project;
          return updated;
        });
      } finally {
        setCurrentProjectLoading(false);
      }
    },
    [fetchJobs]
  );

  const clearCurrentProject = useCallback(() => {
    setCurrentProject(undefined);
    setJobs(null);
  }, []);

  // Re-fetch jobs when sort order or page size changes (project change is handled by fetchCurrentProject)
  useEffect(() => {
    if (currentProject?.id) {
      fetchJobs(currentProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchJobs]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setInProgress(true);
        await fetchProjects(controller);
        setInProgress(false);
      } catch (err) {
        // If it's aborted it's from the unmounting of the component
        if (axios.isCancel(err)) {
          setInProgress(true);
        } else {
          setInProgress(false);
        }
      }
    })();

    return () => {
      controller.abort();
    }
  }, [fetchProjects, user]);

  const value = {
    projects,
    inProgress,
    runningProjectInfo,
    totalCount,
    page,
    setPage,
    orderBy,
    setOrderBy,
    currentProject,
    currentProjectLoading,
    fetchCurrentProject,
    clearCurrentProject,
    jobs,
    jobsLoading,
    jobOrder,
    setJobOrder,
    numberOfJobs,
    setNumberOfJobs,
    fetchJobs,
    // In background means no update of progress
    refreshInBackground: fetchProjects,
  };

  return (
    <HostedScrapingProjectsContext.Provider value={value}>
      {children}
    </HostedScrapingProjectsContext.Provider>
  );
}

export function useHostedScrapingProjects() {
  return useContext(HostedScrapingProjectsContext);
}
